import { mediaUrl } from "@/components/ott/VideoCard";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Star, MapPin, Search as MagnifyingGlass, SlidersHorizontal, ShieldCheck, Crosshair, X, Briefcase, CalendarClock, Heart, ShoppingBag, Phone, ClipboardCheck, BadgeCheck, Film, Grid3x3, User as UserIcon, Layers, MapPinned } from "lucide-react";
import MapView from "@/components/MapView";
import { LiveResults } from "@/components/search/LiveResults";
import CityChanger from "@/components/CityChanger";
import LiveAdsSlot from "@/components/LiveAdsSlot";
import LocationChip from "@/components/LocationChip";
import OffersStrip from "@/components/OffersStrip";
import PortfolioAllocator from "@/components/PortfolioAllocator";
import SmartMatchPanel from "@/components/SmartMatchPanel";
import { useSeo } from "@/hooks/useSeo";
import { api, CATEGORIES, CAT_MAP, priceLabel } from "@/lib/api";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Categories where a geographic map is genuinely useful (physical nearby services).
// Anything NOT in this set is treated as location-agnostic — we hide the map + distance
// controls and let the listings/content fill the full width.
const MAP_CATEGORIES = new Set([
  "service", "restaurant", "property", "hotel", "store", "hospital", "doctor",
  "school", "tutor", "daycare", "salon", "gym", "pet_care", "veterinarian",
  "photographer", "wedding_planner", "elder_care", "physiotherapy", "cab",
  "rental", "marriage_hall", "party_hall", "pharmacy", "baby_care",
]);

/**
 * Category-aware Call-To-Action buttons.
 *
 * Rules (mirrors user brief):
 *   - job / vacancy      → "Apply"
 *   - matrimonial        → "Fix Meeting" (calendar-based)
 *   - dating             → "Date Me"
 *   - shopping/product/rental/property/store/brand → "Buy Now"
 *   - service / doctor / hotel etc → "Book"
 *   - default            → "Contact"
 * Buttons stop event propagation so the outer Link (which navigates to detail) doesn't fire.
 */
function CategoryCTAs({ listing }) {
  const l = listing;
  const navigate = useNavigate();
  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
  const btn = "text-[11px] font-bold rounded-full px-3 py-1.5 inline-flex items-center gap-1 shadow-sm";
  const go = (path) => (e) => { stop(e); navigate(path); };
  const dial = (e) => {
    stop(e);
    if (l.contact_phone || l.mobile) window.location.href = `tel:${l.contact_phone || l.mobile}`;
    else navigate("/messenger");
  };

  if (["job", "vacancy"].includes(l.category)) {
    return (
      <div className="mt-2 flex gap-1.5 flex-wrap" onClick={stop} data-testid={`cta-job-${l.listing_id}`}>
        <button onClick={go(`/listing/${l.listing_id}?action=apply`)} className={`${btn} bg-[#E05E36] text-white hover:bg-[#c94e2a]`}><Briefcase size={12} /> Apply</button>
        <button onClick={dial} className={`${btn} bg-white border border-stone-200 text-stone-700 hover:bg-stone-50`}><Phone size={12} /> Call</button>
      </div>
    );
  }
  if (l.category === "matrimonial") {
    return (
      <div className="mt-2 flex gap-1.5 flex-wrap" onClick={stop} data-testid={`cta-matri-${l.listing_id}`}>
        <button onClick={go(`/listing/${l.listing_id}?action=meeting`)} className={`${btn} bg-rose-600 text-white hover:bg-rose-700`}><CalendarClock size={12} /> Fix Meeting</button>
        <button onClick={dial} className={`${btn} bg-white border border-stone-200 text-stone-700 hover:bg-stone-50`}><Phone size={12} /> Call</button>
      </div>
    );
  }
  if (l.category === "dating") {
    return (
      <div className="mt-2 flex gap-1.5 flex-wrap" onClick={stop} data-testid={`cta-dating-${l.listing_id}`}>
        <button onClick={go("/dating")} className={`${btn} bg-pink-600 text-white hover:bg-pink-700`}><Heart size={12} /> Date Me</button>
      </div>
    );
  }
  if (["shopping", "product", "rental", "property", "store", "brand", "commodity"].includes(l.category)) {
    const nvn = l.owner_nvn || l.owner_id;
    return (
      <div className="mt-2 flex gap-1.5 flex-wrap" onClick={stop} data-testid={`cta-buy-${l.listing_id}`}>
        <button onClick={go(nvn ? `/store/${nvn}` : `/listing/${l.listing_id}`)} className={`${btn} bg-[#E05E36] text-white hover:bg-[#c94e2a]`}><ShoppingBag size={12} /> Buy Now</button>
        <button onClick={dial} className={`${btn} bg-white border border-stone-200 text-stone-700 hover:bg-stone-50`}><Phone size={12} /> Call</button>
      </div>
    );
  }
  if (["service", "doctor", "hospital", "hotel", "salon", "gym", "tutor", "daycare", "photographer", "wedding_planner"].includes(l.category)) {
    return (
      <div className="mt-2 flex gap-1.5 flex-wrap" onClick={stop} data-testid={`cta-book-${l.listing_id}`}>
        <button onClick={go(`/listing/${l.listing_id}?action=book`)} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}><ClipboardCheck size={12} /> Book</button>
        <button onClick={dial} className={`${btn} bg-white border border-stone-200 text-stone-700 hover:bg-stone-50`}><Phone size={12} /> Call</button>
      </div>
    );
  }
  return null;
}

// If no category is picked ("All"), keep the map — it's the main discovery view.
const isMapCategory = (cat) => !cat || MAP_CATEGORIES.has(cat);

// BUG-A2-10: API field is `thumbnail` (thumbnail_url kept for older rows); placeholder when a reel has none.
const ReelThumb = ({ r }) => {
  const src = r.thumbnail_url || r.thumbnail;
  return src
    ? <img src={src} alt="" className="w-full h-full object-cover opacity-90" loading="lazy" />
    : <div className="w-full h-full grid place-items-center bg-gradient-to-br from-stone-700 to-stone-900 text-white/70 text-[10px] font-bold uppercase tracking-wider" data-testid="reel-thumb-placeholder">{r.source || "Reel"}</div>;
};

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFailed, setSearchFailed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const { loc, detect, loading: locLoading } = useUserLocation();

  // Redirect legacy flight-category traffic to the dedicated Flights page.
  const navigate = useNavigate();
  useEffect(() => {
    if (params.get("category") === "flight") {
      navigate("/flights", { replace: true });
    }
  }, [params, navigate]);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const city = params.get("city") || "";
  const cats = params.get("cats") || "";                 // life-stage multi-category filter
  const stage = params.get("stage") || "";
  const sort = params.get("sort") || "";
  const profession = params.get("profession") || "";
  const verifiedCreator = params.get("verified_creator") === "1";
  const radiusParam = params.get("radius");
  const radius = radiusParam ? Number(radiusParam) : 50;

  // Slice G · Universal Search — scope tab strip. Default `all` shows the
  // classic listings grid plus lightweight side-rails for Reels, Verticals
  // and People. Selecting a specific scope switches the main area to that
  // bucket only, powered by the /api/search/universal aggregator.
  const scope = params.get("scope") || "all";
  const isListingsScope = scope === "all" || scope === "listings" || scope === "jobs";
  const effectiveCategory = scope === "jobs" ? (category || "job") : category;
  const [uni, setUni] = useState({ reels: [], verticals: [], people: [], places: [], live: [], videos: [] });

  // Chip-strip categories: hide secondary long-tail categories (flights, trains,
  // buses, movies, party halls, antiques, developers, etc.) from the default
  // primary strip. Show a secondary category ONLY when:
  //   · the user is actively viewing it (?category=flight) — so they can clear
  //   · the raw search text explicitly names it (e.g. "delhi flight", "cinema")
  // Everything else stays reachable via the "More categories ↗" chip that
  // deep-links into AppLauncher's full category grid.
  const visibleCategories = useMemo(() => {
    const qLower = q.toLowerCase();
    const CONTEXT_PATTERNS = {
      flight: /\b(flight|airline|air ticket|airfare|flight status|delhi to |bombay to |to \w+ flight)\b/i,
      train:  /\b(train|rail|irctc|pnr|superfast|express)\b/i,
      bus:    /\b(bus|sleeper|volvo|redbus)\b/i,
      movie:  /\b(movie|cinema|film|imax|multiplex)\b/i,
      music:  /\b(music|concert|gig|album|band)\b/i,
      yojna:  /\b(yojna|yojana|scheme|sarkari|govt scheme)\b/i,
      commodity: /\b(gold|silver|platinum|bullion|mcx)\b/i,
      antique: /\b(antique|vintage|collectible|auction)\b/i,
      developer: /\b(developer|freelancer|software|programmer)\b/i,
      marriage_hall: /\b(marriage hall|wedding venue|banquet)\b/i,
      party_hall: /\b(party hall|birthday venue)\b/i,
    };
    return CATEGORIES.filter(c => {
      if (!c.secondary) return true;                     // always show primary
      if (c.key === category) return true;               // active — keep so user can clear
      const pat = CONTEXT_PATTERNS[c.key];
      if (pat && qLower && pat.test(qLower)) return true; // contextually relevant
      return false;
    });
  }, [q, category]);

  // SEO — dynamic per (category + city + q) combo so every combo gets a unique title
  // and description. Big win for organic-search coverage: "plumber in Bengaluru",
  // "matrimonial in Mumbai", etc all rank on distinct URLs.
  const catLabel = CAT_MAP[category]?.label || category || "";
  const cityLabel = city || "your area";
  useSeo({
    title: q
      ? `${q} near ${cityLabel}`
      : catLabel
        ? `${catLabel} near ${cityLabel}`
        : `Search local — ${cityLabel}`,
    description: catLabel
      ? `Find verified ${catLabel.toLowerCase()} providers near ${cityLabel} on find2near — with ratings, live GPS tracking and secure booking.`
      : `Search verified plumbers, doctors, homes, jobs, matrimonial matches and more near ${cityLabel} on find2near.`,
    canonical: `/search?${params.toString()}`,
  });

  useEffect(() => {
    setLoading(true);
    setSearchFailed(false);
    const failListings = () => { setSearchFailed(true); return []; };
    // If user explicitly selected a city different from their current location, don't clip by proximity.
    const useCoords = !city || (loc.city && city.toLowerCase() === loc.city.toLowerCase());
    const geoParams = useCoords ? { lat: loc.lat, lng: loc.lng, radius_km: radius } : {};
    const commonParams = {
      sort: sort || undefined, profession: profession || undefined,
      verified_creator: verifiedCreator ? true : undefined,
      ...geoParams,
    };

    // Also pull live external POIs (OpenStreetMap / Google Places) for the picked category.
    // These are merged with internal listings, deduped by listing_id, and re-sorted.
    // NOTE: Justdial cannot be scraped (their ToS prohibits it). OSM is free & open;
    // Google Places is used automatically when GOOGLE_MAPS_API_KEY is present on the server.
    const fetchDiscover = (cat) => {
      if (!cat || !useCoords || !loc.lat || !loc.lng) return Promise.resolve([]);
      return api
        .get("/discover/nearby", {
          params: { category: cat, lat: loc.lat, lng: loc.lng, radius_km: radius, limit: 40 },
        })
        .then(r => (r.data?.results || []).map(x => ({ ...x, is_live: true })))
        .catch(() => []);
    };

    const applySort = (items) => {
      if (sort === "price_desc") items.sort((a, b) => (b.price || 0) - (a.price || 0));
      else if (sort === "price_asc") items.sort((a, b) => (a.price || 0) - (b.price || 0));
      else if (sort === "rating_desc") items.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
      else if (sort === "distance_asc" || useCoords) items.sort((a, b) => (a.distance_km ?? 9e9) - (b.distance_km ?? 9e9));
      return items;
    };

    const dedupeMerge = (arrays) => {
      const merged = {};
      arrays.flat().forEach(l => { if (l && l.listing_id) merged[l.listing_id] = l; });
      return Object.values(merged);
    };

    if (cats) {
      const catList = cats.split(",").filter(Boolean);
      Promise.all([
        ...catList.map(c =>
          api.get("/listings", { params: { q, category: c, city, ...commonParams } })
            .then(r => r.data).catch(failListings)
        ),
        ...catList.map(c => fetchDiscover(c)),
      ]).then(arrays => {
        setListings(applySort(dedupeMerge(arrays)));
      }).finally(() => setLoading(false));
    } else {
      Promise.all([
        api.get("/listings", { params: { q, category: effectiveCategory, city, ...commonParams } })
          .then(r => r.data).catch(failListings),
        fetchDiscover(effectiveCategory),
      ]).then(arrays => {
        setListings(applySort(dedupeMerge(arrays)));
      }).finally(() => setLoading(false));
    }
  }, [q, category, effectiveCategory, city, cats, sort, profession, verifiedCreator, radius, loc.lat, loc.lng, loc.city, retryTick]);

  // Slice G · Universal aggregator fetch — populates Reels/Verticals/People
  // side-rails without duplicating the listings request. Runs alongside the
  // main listings effect so failures degrade gracefully.
  useEffect(() => {
    const useCoords = !city || (loc.city && city.toLowerCase() === loc.city.toLowerCase());
    const geoParams = useCoords && loc.lat && loc.lng
      ? { lat: loc.lat, lng: loc.lng, radius_km: radius }
      : {};
    api.get("/search/universal", {
      params: {
        q: q || undefined,
        scope: scope === "listings" ? "all" : scope,       // listings tab still needs side-rails
        category: effectiveCategory || undefined,
        city: city || undefined,
        limit: 12,
        ...geoParams,
      },
    })
      .then(r => setUni({
        reels:     r.data?.reels     || [],
        verticals: r.data?.verticals || [],
        people:    r.data?.people    || [],
        places:    r.data?.places    || [],
        live:      r.data?.live      || [],
        videos:    r.data?.videos    || [],
      }))
      .catch(() => setUni({ reels: [], verticals: [], people: [], places: [], live: [], videos: [] }));
  }, [q, effectiveCategory, city, scope, radius, loc.lat, loc.lng, loc.city]);

  const markers = useMemo(() => listings.map((l, i) => ({
    key: l.listing_id, lat: l.lat, lng: l.lng, label: i + 1,
    popup: <div><b>{l.title}</b><br />{priceLabel(l.price, l.price_unit)}</div>,
  })), [listings]);

  // Add user location marker
  const allMarkers = useMemo(() => [
    ...markers,
    { key: "me", lat: loc.lat, lng: loc.lng, label: "📍", pulse: true, popup: "You are here" },
  ], [markers, loc.lat, loc.lng]);

  const setP = (patch) => {
    const np = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => v ? np.set(k, v) : np.delete(k));
    setParams(np);
  };

  // For non-location categories (matrimonial, jobs, brands, movies, flights, etc.)
  // the map is irrelevant. Hide it and let listings fill the row. Also hide the
  // map when a non-listings scope (reels/verticals/people) is active — those
  // buckets carry no geo pins.
  const showMap = isMapCategory(category) && isListingsScope;

  // Smart Match layer — surfaces AI-ranked top 5 recommendations at the top
  // of Jobs, Property and Doctors listings. The panel scores the *visible*
  // listings against the user's saved smart profile (or an empty profile for
  // logged-out visitors), so it re-ranks in real time as filters change.
  const SMART_KIND_MAP = {
    job: "jobs", vacancy: "jobs",
    property: "property", rental: "property",
    doctor: "doctors", hospital: "doctors",
  };
  const smartKind = SMART_KIND_MAP[category];

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-6">
      {/* Filter row */}
      <div className="bg-white border border-stone-200 rounded-2xl p-3 flex flex-wrap gap-2 items-center sticky top-16 z-30">
        <div data-testid="search-locate-btn">
          <LocationChip compact showAge />
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[220px] bg-stone-50 rounded-xl px-3 py-2">
          <MagnifyingGlass size={16} className="text-stone-500" />
          <input
            key={q}
            defaultValue={q}
            onKeyDown={e => e.key === "Enter" && setP({ q: e.currentTarget.value })}
            placeholder="Search plumber, dentist, 2BHK…"
            className="bg-transparent w-full outline-none text-sm"
            data-testid="search-input"
          />
          {q && (
            <button
              onClick={() => setP({ q: "" })}
              className="text-stone-400 hover:text-[#E05E36]"
              aria-label="Clear search"
              data-testid="search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <CityChanger
          variant="inline"
          initialCity={city || loc.city}
          onGo={(cityName) => setP({ city: cityName })}
        />
        <div className="hidden md:flex items-center gap-1 overflow-x-auto no-scrollbar">
          <button onClick={() => setP({ category: "" })} className={`text-xs font-semibold px-3 py-2 rounded-full border ${!category ? "bg-[#E05E36] text-white border-[#E05E36]" : "bg-white border-stone-200"}`} data-testid="filter-all">All</button>
          {visibleCategories.map(c => (
            <button key={c.key} onClick={() => setP({ category: c.key })} className={`text-xs font-semibold px-3 py-2 rounded-full border whitespace-nowrap ${category === c.key ? "bg-[#E05E36] text-white border-[#E05E36]" : "bg-white border-stone-200 hover:border-stone-300"}`} data-testid={`filter-${c.key}`}>
              {c.label}
            </button>
          ))}
          <Link to="/apps" className="text-xs font-semibold px-3 py-2 rounded-full border bg-stone-50 border-dashed border-stone-300 text-stone-600 hover:border-stone-400 whitespace-nowrap" data-testid="filter-more-categories">More categories ↗</Link>
        </div>
      </div>

      {/* Slice G · Universal Search scope tabs — All / Places / Reels / Jobs / People / Verticals */}
      <div className="mt-3 flex gap-1.5 overflow-x-auto no-scrollbar" data-testid="scope-tabs">
        {[
          { key: "all",       label: "All",        Icon: Layers },
          { key: "places",    label: "Places",     Icon: MapPinned },
          { key: "reels",     label: "Reels",      Icon: Film },
          { key: "jobs",      label: "Jobs",       Icon: Briefcase },
          { key: "people",    label: "People",     Icon: UserIcon },
          { key: "verticals", label: "Verticals",  Icon: Grid3x3 },
          { key: "live",      label: "Live",       Icon: MapPin },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setP({ scope: key === "all" ? "" : key })}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 whitespace-nowrap transition-colors ${
              scope === key || (key === "all" && (!params.get("scope")))
                ? "bg-stone-900 text-white border-stone-900"
                : "bg-white border-stone-200 text-stone-700 hover:border-stone-300"
            }`}
            data-testid={`scope-${key}`}
          >
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      {/* Mobile category scroll */}
      <div className="md:hidden mt-3 flex gap-2 overflow-x-auto no-scrollbar pb-2">
        <button onClick={() => setP({ category: "" })} className={`text-xs font-semibold px-3 py-2 rounded-full border whitespace-nowrap ${!category ? "bg-[#E05E36] text-white border-[#E05E36]" : "bg-white border-stone-200"}`}>All</button>
        {visibleCategories.map(c => (
          <button key={c.key} onClick={() => setP({ category: c.key })} className={`text-xs font-semibold px-3 py-2 rounded-full border whitespace-nowrap ${category === c.key ? "bg-[#E05E36] text-white border-[#E05E36]" : "bg-white border-stone-200"}`}>
            {c.label}
          </button>
        ))}
        <Link to="/apps" className="text-xs font-semibold px-3 py-2 rounded-full border border-dashed border-stone-300 text-stone-600 bg-stone-50 whitespace-nowrap" data-testid="filter-more-categories-mobile">More ↗</Link>
      </div>

      {/* Distance segmentation + sort — distance only makes sense for physical nearby categories */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="segmentation-row">
        {showMap && (
          <>
            <span className="text-[11px] uppercase tracking-widest font-bold text-stone-500 flex items-center gap-1">
              <Crosshair size={12} className="text-[#2D5A27]" /> Within
            </span>
            {[5, 10, 15, 25, 50, 100].map(r => (
              <button
                key={r}
                onClick={() => setP({ radius: r === 50 ? "" : String(r) })}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                  radius === r
                    ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                    : "bg-white border-stone-200 text-stone-700 hover:border-stone-300"
                }`}
                data-testid={`radius-${r}`}
              >
                {r} km
              </button>
            ))}
          </>
        )}
        <span className="ml-auto text-[11px] uppercase tracking-widest font-bold text-stone-500">Sort</span>
        <select
          value={sort}
          onChange={e => setP({ sort: e.target.value })}
          className="text-xs font-bold px-3 py-1.5 rounded-full border border-stone-200 bg-white text-stone-700"
          data-testid="sort-select"
        >
          <option value="">Recommended</option>
          {showMap && <option value="distance_asc">Nearest first</option>}
          <option value="price_asc">Price: low → high</option>
          <option value="price_desc">Price: high → low</option>
          <option value="rating_desc">Best rated</option>
        </select>
        {/* Verified-creator toggle — surfaces the admin-set blue tick from
            listings.extra.influencer.verified. Feed shows only creators with a signed-off
            YouTube/IG/TikTok/X handle so buyers can quickly filter out unverified listings. */}
        <button
          onClick={() => setP({ verified_creator: verifiedCreator ? "" : "1" })}
          className={`text-xs font-bold px-3 py-1.5 rounded-full border inline-flex items-center gap-1 transition-colors ${
            verifiedCreator
              ? "bg-sky-500 text-white border-sky-500"
              : "bg-white border-stone-200 text-stone-700 hover:border-sky-300"
          }`}
          data-testid="filter-verified-creator"
          title="Show only verified creators"
        >
          <BadgeCheck size={12} /> Verified creator
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 min-w-0">
        <div className={`min-w-0 ${showMap ? "lg:col-span-7" : "lg:col-span-12"}`}>
          {/* Portfolio Allocator — only shown when browsing Investments */}
          {category === "investment" && (
            <div className="mb-5" data-testid="portfolio-allocator-wrap">
              <PortfolioAllocator />
            </div>
          )}

          {/* Active filter chips — makes it obvious what's narrowing the results */}
          {(q || category || city || stage) && (
            <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="active-filters">
              <span className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Filters:</span>
              {stage && (
                <button
                  onClick={() => setP({ stage: "", cats: "" })}
                  className="inline-flex items-center gap-1 text-xs font-bold bg-[#DDA77B]/20 text-[#8B5E34] border border-[#DDA77B]/40 rounded-full pl-3 pr-2 py-1"
                  data-testid="chip-clear-stage"
                >
                  Life-stage · {stage} <X size={12} />
                </button>
              )}
              {q && (
                <button
                  onClick={() => setP({ q: "" })}
                  className="inline-flex items-center gap-1 text-xs font-bold bg-[#E05E36]/10 text-[#E05E36] border border-[#E05E36]/30 rounded-full pl-3 pr-2 py-1"
                  data-testid="chip-clear-q"
                >
                  “{q}” <X size={12} />
                </button>
              )}
              {category && (
                <button
                  onClick={() => setP({ category: "" })}
                  className="inline-flex items-center gap-1 text-xs font-bold bg-[#2D5A27]/10 text-[#2D5A27] border border-[#2D5A27]/30 rounded-full pl-3 pr-2 py-1"
                  data-testid="chip-clear-category"
                >
                  {CAT_MAP[category]?.label || category} <X size={12} />
                </button>
              )}
              {city && (
                <button
                  onClick={() => setP({ city: "" })}
                  className="inline-flex items-center gap-1 text-xs font-bold bg-stone-100 text-stone-700 border border-stone-200 rounded-full pl-3 pr-2 py-1"
                  data-testid="chip-clear-city"
                >
                  <MapPin size={11} /> {city} <X size={12} />
                </button>
              )}
              <button
                onClick={() => setParams(new URLSearchParams())}
                className="text-[11px] font-bold text-stone-500 hover:text-[#E05E36] underline underline-offset-4"
                data-testid="clear-all-filters"
              >
                Clear all
              </button>
            </div>
          )}

          {searchFailed && !loading && (
            <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-3" data-testid="search-error">
              Search failed. Results below may be incomplete; this is not an empty result.
              <button onClick={() => setRetryTick(t => t + 1)} className="h-8 px-3 rounded-full border border-red-300 font-bold" data-testid="search-retry">Retry</button>
            </div>
          )}
          <div className="mb-3 text-sm text-stone-600" data-testid="result-count">
            {loading ? "Searching..." : (
              scope === "reels"     ? <><b>{uni.reels.length}</b> reels</> :
              scope === "places"    ? <><b>{uni.places.length}</b> places</> :
              scope === "people"    ? <><b>{uni.people.length}</b> people</> :
              scope === "verticals" ? <><b>{uni.verticals.length}</b> verticals</> :
              scope === "live"      ? <><b>{uni.live.length}</b> live results</> :
              <><b>{listings.length}</b> results {category && <>in <b>{CAT_MAP[category]?.label || category}</b></>}</>
            )}
          </div>

          {/* Offers strip — SmartBuy-style promo carousel. Filtered by the
              active category chip; auto-hides when there are no active offers. */}
          <OffersStrip scope="search" category={category || null} className="mb-4" />

          {/* Sponsored "top listings" — paid boost that pins listings above organic
              search results. Also renders a category banner ad below it. */}
          {category && (
            <>
              <LiveAdsSlot slot="top_listing" category={category} city={city || undefined} limit={2} className="mb-4" />
              <LiveAdsSlot slot="banner" category={category} className="mb-4" />
            </>
          )}

          {/* Smart Match — top 5 AI-ranked results across the current visible
              listings. Only rendered for Jobs, Property and Doctors where a
              scorer exists on the backend. */}
          {smartKind && !loading && listings.length > 0 && (
            <div className="mb-4" data-testid={`smart-wrap-${smartKind}`}>
              <SmartMatchPanel
                kind={smartKind}
                listings={listings.slice(0, 30).map(l => ({
                  id:                    l.listing_id,
                  title:                 l.title,
                  name:                  l.title,
                  city:                  l.city || l.extra?.city,
                  price_inr:             (l.price || 0) / 100,
                  rent_inr:              l.price_unit === "monthly" ? (l.price || 0) / 100 : undefined,
                  bedrooms:              l.extra?.bedrooms,
                  amenities:             l.extra?.amenities || l.tags,
                  nearby_schools:        l.extra?.nearby_schools,
                  skills:                l.extra?.skills || l.tags,
                  salary_min_inr:        l.extra?.salary_min_inr,
                  salary_max_inr:        l.extra?.salary_max_inr,
                  experience_years_min:  l.extra?.experience_years_min,
                  treats:                l.extra?.treats || l.tags,
                  consult_modes:         l.extra?.consult_modes,
                  insurance_accepted:    l.extra?.insurance_accepted,
                  rating_avg:            l.rating_avg,
                  experience_years:      l.extra?.experience_years,
                  badges:                l.verified ? ["identity_verified", ...(l.extra?.badges || [])]
                                                    : (l.extra?.badges || []),
                }))}
                title={`Top matches — ${CAT_MAP[category]?.label || smartKind}`}
              />
            </div>
          )}

          {/* Slice G · Universal Search — cross-vertical side rails (scope=all only).
              These are lightweight strips so the primary listings area is not
              distracted. Selecting a specific scope hides them. */}
          <div className="mb-3 flex gap-3 text-xs font-bold" data-testid="uni-surface-links">
            <Link to={`/nearby${q ? `?q=${encodeURIComponent(q)}` : ""}`} className="text-sky-700 underline" data-testid="uni-open-map">See this on the map ↗</Link>
          </div>
          {scope === "all" && <LiveResults rows={uni.live} q={q} />}
          {scope === "live" && <LiveResults rows={uni.live} q={q} full />}
          {scope === "all" && uni.verticals.length > 0 && (
            <div className="mb-4" data-testid="uni-verticals-rail">
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2 flex items-center gap-1">
                <Grid3x3 size={12} /> Verticals
              </div>
              <div className="flex gap-2 flex-wrap">
                {uni.verticals.slice(0, 8).map(v => (
                  <Link
                    key={v.id}
                    to={v.to || "/apps"}
                    className="text-xs font-bold px-3 py-1.5 rounded-full border bg-white border-stone-200 hover:border-stone-300 text-stone-700"
                    data-testid={`uni-vertical-${v.id}`}
                  >
                    {v.label}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {scope === "all" && uni.reels.length > 0 && (
            <div className="mb-4" data-testid="uni-reels-rail">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 flex items-center gap-1">
                  <Film size={12} /> Reels
                </div>
                <Link to="/reels" className="text-[11px] font-bold text-[#E05E36]" data-testid="uni-reels-more">See all →</Link>
              </div>
              <div className="flex gap-3 overflow-x-auto no-scrollbar">
                {uni.reels.slice(0, 8).map(r => (
                  <Link
                    key={r.video_id}
                    to={`/reels?video=${r.video_id}`}
                    className="shrink-0 w-32 bg-black rounded-xl overflow-hidden relative"
                    data-testid={`uni-reel-${r.video_id}`}
                  >
                    <div className="aspect-[9/16] bg-stone-900">
                      <ReelThumb r={r} />
                    </div>
                    <div className="absolute bottom-0 inset-x-0 p-2 text-[10px] font-bold text-white line-clamp-2 bg-gradient-to-t from-black/80 to-transparent">
                      {r.title}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {scope === "all" && uni.videos.length > 0 && (
            <div className="mb-4" data-testid="uni-videos-rail">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 flex items-center gap-1">
                  <Film size={12} /> Videos
                </div>
                <Link to="/ott" className="text-[11px] font-bold text-[#E05E36]" data-testid="uni-videos-more">See all →</Link>
              </div>
              <div className="flex gap-3 overflow-x-auto no-scrollbar">
                {uni.videos.slice(0, 6).map(v => (
                  <Link key={v.video_id} to={`/ott/watch/${v.video_id}`} className="shrink-0 w-40" data-testid={`uni-video-${v.video_id}`}>
                    <div className="aspect-video bg-stone-200 rounded-xl overflow-hidden">
                      {v.thumb_url && <img src={mediaUrl(v.thumb_url)} alt="" className="w-full h-full object-cover" loading="lazy" />}
                    </div>
                    <div className="text-[11px] font-bold text-stone-800 line-clamp-1 mt-1">{v.title}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {scope === "all" && uni.people.length > 0 && (
            <div className="mb-4" data-testid="uni-people-rail">
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2 flex items-center gap-1">
                <UserIcon size={12} /> People
              </div>
              <div className="flex gap-2 flex-wrap">
                {uni.people.slice(0, 6).map(p => (
                  <div
                    key={p.entry_id}
                    className="text-xs bg-white border border-stone-200 rounded-full px-3 py-1.5 inline-flex items-center gap-2"
                    data-testid={`uni-person-${p.entry_id}`}
                  >
                    <span className="font-bold text-stone-700">{p.name || "Unnamed"}</span>
                    {p.city && <span className="text-stone-500">· {p.city}</span>}
                    {p.mobile_masked && <span className="text-stone-400">· {p.mobile_masked}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reels-only tab: full grid of the aggregator's reels bucket */}
          {scope === "reels" && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="scope-reels-grid">
              {uni.reels.map(r => (
                <Link
                  key={r.video_id}
                  to={`/reels?video=${r.video_id}`}
                  className="block bg-black rounded-xl overflow-hidden relative"
                  data-testid={`reel-card-${r.video_id}`}
                >
                  <div className="aspect-[9/16] bg-stone-900">
                    <ReelThumb r={r} />
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-2 text-xs font-bold text-white line-clamp-2 bg-gradient-to-t from-black/90 to-transparent">
                    {r.title}
                  </div>
                </Link>
              ))}
              {uni.reels.length === 0 && (
                <div className="col-span-full text-center py-12 bg-white border border-stone-200 rounded-2xl" data-testid="scope-reels-empty">
                  <Film size={28} className="mx-auto text-stone-300 mb-3" />
                  <div className="text-stone-700 font-bold">No reels match this search</div>
                  <div className="text-sm text-stone-500 mt-1">Try a different keyword or clear the filter.</div>
                </div>
              )}
            </div>
          )}

          {/* Verticals-only tab: full grid of hub catalogue hits */}
          {scope === "verticals" && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="scope-verticals-grid">
              {uni.verticals.map(v => (
                <Link
                  key={v.id}
                  to={v.to || "/apps"}
                  className="block bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300 hover:shadow-md transition-shadow"
                  data-testid={`vertical-card-${v.id}`}
                >
                  <div className="text-sm font-bold text-stone-900">{v.label}</div>
                  <div className="text-xs text-stone-500 mt-1">{v.group}</div>
                </Link>
              ))}
              {uni.verticals.length === 0 && (
                <div className="col-span-full text-center py-12 bg-white border border-stone-200 rounded-2xl" data-testid="scope-verticals-empty">
                  <Grid3x3 size={28} className="mx-auto text-stone-300 mb-3" />
                  <div className="text-stone-700 font-bold">No verticals match this search</div>
                </div>
              )}
            </div>
          )}

          {/* People-only tab: directory search results */}
          {scope === "people" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="scope-people-grid">
              {uni.people.map(p => (
                <div key={p.entry_id} className="bg-white border border-stone-200 rounded-2xl p-4" data-testid={`person-card-${p.entry_id}`}>
                  <div className="font-bold text-stone-900">{p.name || "Unnamed"}</div>
                  <div className="text-xs text-stone-500 mt-1">
                    {[p.city, p.category, p.source].filter(Boolean).join(" · ")}
                  </div>
                  {p.mobile_masked && (
                    <div className="mt-2 text-xs font-mono text-stone-600">{p.mobile_masked}</div>
                  )}
                </div>
              ))}
              {uni.people.length === 0 && (
                <div className="col-span-full text-center py-12 bg-white border border-stone-200 rounded-2xl" data-testid="scope-people-empty">
                  <UserIcon size={28} className="mx-auto text-stone-300 mb-3" />
                  <div className="text-stone-700 font-bold">No people match this search</div>
                  <div className="text-sm text-stone-500 mt-1">Try searching by name or mobile.</div>
                </div>
              )}
            </div>
          )}

          {/* Places-only tab: dedicated POIs list */}
          {scope === "places" && (
            <div className="space-y-3" data-testid="scope-places-list">
              {uni.places.map((p, i) => (
                <div key={p.listing_id || i} className="bg-white border border-stone-200 rounded-2xl p-4" data-testid={`place-card-${p.listing_id || i}`}>
                  <div className="font-bold text-stone-900">{p.title || p.name}</div>
                  <div className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                    <MapPin size={11} /> {p.address || p.city}
                    {p.distance_km !== undefined && <span>· {Number(p.distance_km).toFixed(2)} km</span>}
                  </div>
                  <span className="inline-flex items-center gap-1 mt-2 bg-[#2D5A27] text-white px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider">
                    <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> Live
                  </span>
                </div>
              ))}
              {uni.places.length === 0 && (
                <div className="text-center py-12 bg-white border border-stone-200 rounded-2xl" data-testid="scope-places-empty">
                  <MapPinned size={28} className="mx-auto text-stone-300 mb-3" />
                  <div className="text-stone-700 font-bold">No places found near you</div>
                  <div className="text-sm text-stone-500 mt-1">
                    {loc.lat ? "Try widening the radius or picking a category." : "Enable location to find places nearby."}
                  </div>
                </div>
              )}
            </div>
          )}

          {isListingsScope && (
          <div className={showMap ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"}>
            {listings.map((l, i) => (
              <Link key={l.listing_id} to={`/listing/${l.listing_id}`} className="block bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300 hover:shadow-md transition-shadow" data-testid={`result-${l.listing_id}`}>
                <div className="flex gap-4">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden bg-stone-100 shrink-0 relative">
                    {l.images?.[0] && <img loading="lazy" src={l.images[0]} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute top-1.5 left-1.5 bg-[#E05E36] text-white text-[10px] font-bold w-6 h-6 rounded-full grid place-items-center">{i + 1}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-1.5">
                          {l.category}
                          {l.is_live && (
                            <span
                              className="inline-flex items-center gap-1 bg-[#2D5A27] text-white px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider"
                              data-testid={`live-badge-${l.listing_id}`}
                              title={`Live from ${l.source || "public map data"}`}
                            >
                              <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> Live
                            </span>
                          )}
                        </div>
                        <div className="heading font-bold text-lg text-stone-900 line-clamp-1">{l.title}</div>
                      </div>
                      {l.verified && <div className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-[#2D5A27] bg-[#2D5A27]/10 px-2 py-1 rounded-full"><ShieldCheck size={12} /> Verified</div>}
                    </div>
                    <div className="text-sm text-stone-600 line-clamp-2 mt-1">{l.description}</div>

                    {/* Category-specific inline info strip */}
                    {(["matrimonial", "dating", "job"].includes(l.category) && l.extra) && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                        {l.extra.dob && (() => {
                          try {
                            const d = new Date(l.extra.dob);
                            const now = new Date();
                            let age = now.getFullYear() - d.getFullYear();
                            if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age--;
                            return <span className="font-bold text-[#2D5A27]">{age} yrs</span>;
                          } catch { return null; }
                        })()}
                        {l.tags?.filter(t => !["bride", "groom", "male", "female", "engineer", "live", "24k"].includes(t)).slice(0, 2).map(t => (
                          <span key={t} className="text-stone-600">· {t.replace(/_/g, " ")}</span>
                        ))}
                        {l.extra.annual_income_inr > 0 && (
                          <span className="text-stone-500">
                            · ₹{(l.extra.annual_income_inr / 100000).toFixed(l.extra.annual_income_inr >= 1000000 ? 0 : 1)}L p.a.
                          </span>
                        )}
                      </div>
                    )}

                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-500">
                      <div className="flex items-center gap-1"><MapPin size={13} /> {l.city}{l.distance_km !== undefined && ` · ${Number(l.distance_km).toFixed(2)} km`}</div>
                      <div className="flex items-center gap-1"><Star size={13} className="text-[#DDA77B]" /> {l.rating_avg} ({l.rating_count})</div>
                      {l.experience_years > 0 && !["matrimonial", "dating", "job"].includes(l.category) && <div>{l.experience_years} yrs exp</div>}
                    </div>

                    {/* Commodity live-price strip */}
                    {l.category === "commodity" && l.live_price ? (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="heading font-bold text-[#E05E36]">
                          ₹{Number(l.live_price).toLocaleString("en-IN", { maximumFractionDigits: 2 })} / {l.extra?.unit_label || "gram"}
                        </span>
                        <span
                          className="inline-flex items-center gap-1 bg-[#2D5A27] text-white px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider"
                          data-testid={`commodity-live-${l.listing_id}`}
                        >
                          <span className="w-1 h-1 rounded-full bg-[#DDA77B] animate-pulse" /> Live
                        </span>
                        {l.market_base && (
                          <span className="text-[10px] text-stone-400">
                            spot ₹{Number(l.market_base).toLocaleString("en-IN", { maximumFractionDigits: 0 })}/g +{l.extra?.margin_pct}%
                          </span>
                        )}
                      </div>
                    ) : ["matrimonial", "dating", "job"].includes(l.category) ? (
                      // Person profiles don't have a "price" — show a subtle "Contact" affordance instead.
                      <div className="mt-2 text-sm font-bold text-[#E05E36]">Contact profile →</div>
                    ) : (
                      <div className="mt-2 heading font-bold text-[#E05E36]">{priceLabel(l.price, l.price_unit)}</div>
                    )}

                    {/* Category-aware CTA row */}
                    <CategoryCTAs listing={l} />
                  </div>
                </div>
              </Link>
            ))}
            {!loading && listings.length === 0 && (
              <div className="text-center py-12 bg-white border border-stone-200 rounded-2xl" data-testid="empty-state">
                <MagnifyingGlass size={28} className="mx-auto text-stone-300 mb-3" />
                {CAT_MAP[category]?.secondary ? (
                  <>
                    <div className="text-stone-700 font-bold">
                      No {CAT_MAP[category].label.toLowerCase()} listings found nearby yet.
                    </div>
                    <div className="text-sm text-stone-500 mt-1">
                      This is a lighter-weight Find2Near category — try the dedicated Travel search or clear the filter.
                    </div>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      {["flight", "train", "bus", "hotel"].includes(category) && (
                        <Link
                          to={category === "flight" ? "/flights" : "/travel"}
                          className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white text-xs font-bold px-4 py-2 inline-flex items-center gap-1"
                          data-testid="empty-explore-travel"
                        >
                          {category === "flight" ? "Search Flights →" : "Explore Travel →"}
                        </Link>
                      )}
                      <Button
                        onClick={() => setP({ category: "" })}
                        variant="outline"
                        className="rounded-full text-xs font-bold px-4 py-2"
                        data-testid="empty-clear-category"
                      >
                        Clear filter
                      </Button>
                      <Link to="/apps" className="rounded-full border border-stone-200 hover:border-stone-300 text-xs font-bold px-4 py-2 inline-flex items-center gap-1 text-stone-700" data-testid="empty-more-categories">More categories ↗</Link>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-stone-700 font-bold">No results match all your filters</div>
                    <div className="text-sm text-stone-500 mt-1">
                      {q && category
                        ? <>“<b>{q}</b>” inside <b>{CAT_MAP[category]?.label || category}</b> returned nothing.</>
                        : q
                          ? <>No listings matched “<b>{q}</b>”.</>
                          : <>Try a different city or category.</>}
                    </div>
                    {(q || category || city) && (
                      <Button
                        onClick={() => setParams(new URLSearchParams())}
                        className="mt-4 rounded-full bg-[#E05E36] hover:bg-[#c94e2a]"
                        data-testid="empty-clear-all"
                      >
                        Clear all filters
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          )}
        </div>

        {showMap && (
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32 h-[400px] lg:h-[calc(100vh-9rem)]" data-testid="search-map-wrap">
              <MapView center={[loc.lat, loc.lng]} markers={allMarkers} zoom={12} height="100%" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
